import axiosInstance, { getApiErrorMessage } from "../auth/apiConfig";

const WEB_BASE = "/api/v1/web/dashboard";

const n = (value) => (value == null || Number.isNaN(Number(value)) ? 0 : Number(value));

export const mapDashboardResponse = (payload) => {
  const summary = payload?.summary || {};
  const wards = Array.isArray(payload?.wards) ? payload.wards : [];
  const priorityQueue = Array.isArray(payload?.priorityQueue) ? payload.priorityQueue : [];

  const admittedPatients = n(summary.admittedPatients);
  const roundsCompleted = n(summary.roundsCompleted);
  const roundsPending = n(summary.roundsPending);
  const tprRecorded = n(summary.tprRecorded);
  const tprDue = n(summary.tprDue);
  const intakeOutputRecorded = n(summary.intakeOutputRecorded);
  const intakeOutputDue = n(summary.intakeOutputDue);
  const pendingDischarges = n(summary.pendingDischarges);
  const dischargeSummariesSaved = n(summary.dischargeSummariesSaved);
  const opdPatients = n(summary.opdPatients);
  const otCases = n(summary.otCases);
  const pathologyPending = n(summary.pathologyPending);
  const purchaseOrdersPending = n(summary.purchaseOrdersPending);

  return {
    snapshotDate: summary.snapshotDate || new Date().toISOString().slice(0, 10),
    summary: {
      opdPatients,
      admittedPatients,
      pendingDischarges,
      otCases,
      pendingApprovals: n(summary.pendingApprovals),
    },
    workflowCards: [
      {
        id: "round",
        title: "Dr. Round",
        value: roundsCompleted,
        detail: `${roundsPending} patients pending`,
        tone: "teal",
        icon: "user-doctor",
        path: "/dr-round",
      },
      {
        id: "ot",
        title: "OT Note",
        value: otCases,
        detail: "Scheduled today",
        tone: "blue",
        icon: "procedures",
        path: "/ot-note",
      },
      {
        id: "discharge",
        title: "Discharge Summary",
        value: pendingDischarges,
        detail: `${dischargeSummariesSaved} saved today`,
        tone: "orange",
        icon: "file-medical",
        path: "/discharge-summary",
      },
      {
        id: "records",
        title: "Medical Records",
        value: opdPatients,
        detail: "OPD visits today",
        tone: "purple",
        icon: "folder-open",
        path: "/medical-records",
      },
      {
        id: "pathology",
        title: "Pathology Approval",
        value: pathologyPending,
        detail: "Results awaiting review",
        tone: "red",
        icon: "microscope",
        path: "/pathology-result-approval",
      },
      {
        id: "purchase",
        title: "PO Approval",
        value: purchaseOrdersPending,
        detail: "Orders awaiting approval",
        tone: "amber",
        icon: "file-invoice",
        path: "/purchase-order-approval",
      },
      {
        id: "intake",
        title: "Intake / Output",
        value: intakeOutputRecorded,
        detail: `${intakeOutputDue} charts due`,
        tone: "cyan",
        icon: "chart-line",
        path: "/intake-output-chart",
      },
      {
        id: "tpr",
        title: "TPR",
        value: tprRecorded,
        detail: `${tprDue} observations due`,
        tone: "rose",
        icon: "heartbeat",
        path: "/tpr",
      },
      {
        id: "nursing",
        title: "Nursing Notes",
        value: n(summary.nursingNotes),
        detail: "Entries today",
        tone: "green",
        icon: "user-nurse",
        path: "/nursing-notes",
      },
      {
        id: "rmo",
        title: "RMO Notes",
        value: n(summary.rmoNotes),
        detail: "Entries today",
        tone: "slate",
        icon: "stethoscope",
        path: "/rmo-notes",
      },
    ],
    priorityQueue: priorityQueue.map((item, index) => ({
      id: `${item.sourceType || "item"}-${item.sourceId || index}`,
      patientName: item.patientName || "-",
      reference: item.reference || "-",
      task: item.task || "-",
      urgency: (item.urgency || "normal").toLowerCase(),
      path: item.pathHint || "/dashboard",
    })),
    wards: wards.map((ward) => ({
      wardId: ward.wardId,
      wardName: ward.wardName || "Ward",
      total: n(ward.totalBeds),
      occupied: n(ward.occupiedBeds),
      available: n(ward.availableBeds),
      occupancyPercent: n(ward.occupancyPercent),
    })),
    occupancy: {
      occupied: n(summary.occupiedBeds),
      available: n(summary.availableBeds),
      total: n(summary.totalBeds),
      percent: n(summary.occupancyPercent),
    },
    compliance: [
      {
        label: "Doctor rounds",
        completed: roundsCompleted,
        total: admittedPatients,
        path: "/dr-round",
      },
      {
        label: "TPR observations",
        completed: tprRecorded,
        total: admittedPatients,
        path: "/tpr",
      },
      {
        label: "Intake / Output",
        completed: intakeOutputRecorded,
        total: admittedPatients,
        path: "/intake-output-chart",
      },
      {
        label: "Discharge summaries",
        completed: Math.max(admittedPatients - pendingDischarges, 0),
        total: admittedPatients,
        path: "/discharge-summary",
      },
    ],
  };
};

export const fetchDoctorDashboard = async (asOfDate) => {
  try {
    const response = await axiosInstance.get(WEB_BASE, {
      params: asOfDate ? { asOfDate } : undefined,
    });
    return mapDashboardResponse(response.data?.data);
  } catch (error) {
    throw new Error(getApiErrorMessage(error, "Failed to load dashboard"));
  }
};
